<template>
  <section v-if="fields" :class="$style['c-grid-card-section']" ref="sectionRef">
    <ContentGrid :desktop-default-grid="[2, 20]" :mobile-default-grid="[1, 14]">
      <div :class="$style['c-grid-card-section__inner']">
        <header
          ref="headerRef"
          :class="[
            $style['c-grid-card-section__header'],
            isHeaderVisible && $style['c-grid-card-section__header--revealed'],
          ]"
        >
          <h2
            :class="$style['c-grid-card-section__title']"
            v-if="utils.sc.getFieldValue(fields, 'sectionTitle', 'Resources')"
          >
            {{ utils.sc.getFieldValue(fields, 'sectionTitle', 'Resources') }}
          </h2>
          <p :class="$style['c-grid-card-section__subtitle']">
            {{
              utils.sc.getFieldValue(
                fields,
                'sectionSubtitle',
                'Access technical documentation, catalogs, and guides'
              )
            }}
          </p>
        </header>

        <div
          ref="contentRef"
          :class="[
            $style['c-grid-card-section__content'],
            isContentVisible && $style['c-grid-card-section__content--revealed'],
          ]"
        >
          <GridCardSectionDesktop
            :cards="cards"
            :is-carousel="isCarousel"
            :desktop-column-count="desktopColumnCount"
            :instance-key="instanceKey"
          />
          <GridCardSectionMobile
            :cards="cards"
            :is-carousel="isCarousel"
            :mobile-cards-per-page="mobileCardsPerPage"
            :instance-key="instanceKey"
          />
        </div>
      </div>
    </ContentGrid>
  </section>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';
import utils from '@/utils';
import { GridCardSectionProps } from '@/types/components/grid-card-section';
import GridCardSectionDesktop from '@/component-sections/GridCardSection/GridCardSectionDesktop.vue';
import GridCardSectionMobile from '@/component-sections/GridCardSection/GridCardSectionMobile.vue';

const props = defineProps<GridCardSectionProps>();
const fields = computed(() => props.fields);
const sectionRef = ref<HTMLElement | null>(null);
const headerRef = ref<HTMLElement | null>(null);
const contentRef = ref<HTMLElement | null>(null);
const isHeaderVisible = ref(false);
const isContentVisible = ref(false);

const cards = computed<any[]>(() => {
  const list = utils.sc.getFieldObj(fields.value, 'cards');
  return Array.isArray(list) ? list : [];
});

const normalizeBoolean = (raw: unknown) => {
  if (typeof raw === 'boolean') return raw;
  if (typeof raw === 'number') return raw === 1;
  if (typeof raw === 'string') return ['1', 'true', 'yes', 'on'].includes(raw.toLowerCase());
  return false;
};

const normalizeCount = (raw: unknown, fallback: number) => {
  const parsed = Number.parseInt(String(raw), 10);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.min(Math.max(parsed, 1), 4);
};

const isCarousel = computed(() =>
  normalizeBoolean(utils.sc.getFieldObj(fields.value, 'isCarousel')?.value)
);

const desktopColumnCount = computed(() => {
  const raw = utils.sc.getSpVal(fields.value, 'desktopColumnCount');
  return normalizeCount(raw, 2);
});

const mobileCardsPerPage = computed(() => {
  const raw = utils.sc.getSpVal(fields.value, 'mobileCarsPerPage');
  return normalizeCount(raw, 1);
});

const instanceKey = computed(() =>
  String(props.rendering?.uid || props.dataSource || 'gcs').replace(/[^a-zA-Z0-9_-]/g, '')
);

let headerObserver: IntersectionObserver | null = null;
let contentObserver: IntersectionObserver | null = null;

onMounted(() => {
  if (headerRef.value) {
    headerObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          isHeaderVisible.value = entry.isIntersecting;
        });
      },
      { threshold: 0.1 }
    );
    headerObserver.observe(headerRef.value);
  }

  if (contentRef.value) {
    contentObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          isContentVisible.value = entry.isIntersecting;
        });
      },
      { threshold: 0.1 }
    );
    contentObserver.observe(contentRef.value);
  }
});

onUnmounted(() => {
  if (headerObserver) headerObserver.disconnect();
  if (contentObserver) contentObserver.disconnect();
});
</script>

<style module lang="scss">
@import '../../styles/variables';
.c-grid-card-section {
  padding: 26px 0 42px;
}

.c-grid-card-section__inner {
  width: min(1440px, calc(100% - 56px));
  margin: 0 auto;
  background: $white;
  border-radius: 4px;
  padding: 26px;
}

.c-grid-card-section__header {
  margin-top: 8px;
  margin-bottom: 40px;
  text-align: center;

  .c-grid-card-section__title,
  .c-grid-card-section__subtitle {
    opacity: 0;
    transform: translateY(24px);
    filter: blur(8px);
  }
}

.c-grid-card-section__header--revealed {
  .c-grid-card-section__title {
    animation: gcs-reveal-up 820ms cubic-bezier(0.18, 0.8, 0.2, 1) forwards;
    animation-delay: 120ms;
  }
  .c-grid-card-section__subtitle {
    animation: gcs-reveal-up 820ms cubic-bezier(0.18, 0.8, 0.2, 1) forwards;
    animation-delay: 220ms;
  }
}

.c-grid-card-section__content {
  opacity: 0;
  transform: translateY(24px);
  filter: blur(8px);
}

.c-grid-card-section__content--revealed {
  animation: gcs-reveal-up 860ms cubic-bezier(0.18, 0.8, 0.2, 1) forwards;
  animation-delay: 260ms;
}

.c-grid-card-section__title {
  margin: 0;
  color: #060b17;
  font-size: clamp(30px, 2.2vw, 42px);
  line-height: 1.2;
  font-weight: 700;
}

.c-grid-card-section__subtitle {
  margin: 8px 0 0;
  color: #6c7385;
  font-size: clamp(13px, 1vw, 16px);
  line-height: 1.35;
}

@media (max-width: 768px) {
  .c-grid-card-section {
    padding: 18px 0 26px;
  }

  .c-grid-card-section__inner {
    width: min(350px, calc(100% - 16px));
    padding: 14px 10px;
  }

  .c-grid-card-section__header {
    margin-top: 6px;
  }

  .c-grid-card-section__title {
    font-size: 24px;
  }

  .c-grid-card-section__subtitle {
    margin-top: 6px;
    font-size: 10px;
  }
}

@keyframes gcs-reveal-up {
  0% {
    opacity: 0;
    transform: translateY(24px);
    filter: blur(8px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
    filter: blur(0);
  }
}
</style>
